/** 同期エンジン（仕様書 §7.1・§7.2）。Push → Pull の順に、多重起動しないよう直列で動かす。 */

import { useSyncExternalStore } from 'react';
import { getDb, getMeta, setMeta, type OutboxOp } from '../db/schema';
import { bumpData, onOutboxChanged } from '../db/events';
import { OfflineError, ReauthRequiredError } from '../auth/TokenProvider';
import { finalizeIndexes, runOp } from './push';
import { bootstrap, pullDelta, refetchAll } from './pull';
import type { MonthKey } from '../domain/types';

export type SyncState = 'idle' | 'syncing' | 'offline' | 'reauth' | 'error';

export interface SyncStatus {
  state: SyncState;
  pending: number;
  failed: number;
  conflicts: number;
  lastSyncAt: string | null;
  message: string | null;
  error: string | null;
}

const BACKOFF_MS = [5_000, 15_000, 45_000, 120_000, 300_000, 900_000];
const MAX_ATTEMPTS = 5;
const PERIODIC_MS = 5 * 60_000;
const DEBOUNCE_MS = 1_500;
const VISIBILITY_MIN_GAP_MS = 60_000;

let status: SyncStatus = {
  state: 'idle',
  pending: 0,
  failed: 0,
  conflicts: 0,
  lastSyncAt: null,
  message: null,
  error: null,
};

const listeners = new Set<() => void>();
function emit(patch: Partial<SyncStatus>) {
  status = { ...status, ...patch };
  listeners.forEach((l) => l());
}

export function useSyncStatus(): SyncStatus {
  return useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      return () => listeners.delete(cb);
    },
    () => status,
  );
}

async function refreshCounts(): Promise<void> {
  const db = await getDb();
  emit({
    pending: await db.countFromIndex('outbox', 'by_status', 'pending'),
    failed: await db.countFromIndex('outbox', 'by_status', 'failed'),
    conflicts: ((await db.get('meta', 'conflictCount'))?.value as number) ?? 0,
  });
}

let running = false;
let queuedRun = false;

/** Push: outbox を opId 昇順に処理する。media.put は同一記事の entry.upsert より先。 */
async function push(): Promise<void> {
  const db = await getDb();
  const touched = new Set<MonthKey>();
  const now = Date.now();

  const all = (await db.getAll('outbox')).sort((a, b) => (a.opId ?? 0) - (b.opId ?? 0));
  const ready = all.filter(
    (op) => op.status !== 'failed' && Date.parse(op.nextAttemptAt) <= now,
  );
  // 同じ記事のメディアを先に上げる
  ready.sort((a, b) => rank(a) - rank(b));

  for (const op of ready) {
    try {
      await runOp(op, touched);
      await db.delete('outbox', op.opId!);
    } catch (e) {
      if (e instanceof ReauthRequiredError || e instanceof OfflineError) throw e;
      const attempts = op.attempts + 1;
      const wait = BACKOFF_MS[Math.min(attempts - 1, BACKOFF_MS.length - 1)];
      await db.put('outbox', {
        ...op,
        attempts,
        status: attempts >= MAX_ATTEMPTS ? 'failed' : 'pending',
        nextAttemptAt: new Date(Date.now() + wait).toISOString(),
        lastError: e instanceof Error ? e.message : String(e),
      });
    }
    await refreshCounts();
  }

  if (touched.size > 0) await finalizeIndexes(touched);
}

function rank(op: OutboxOp): number {
  if (op.type === 'media.put') return 0;
  if (op.type === 'entry.upsert') return 1;
  return 2;
}

export async function sync(opts: { manual?: boolean } = {}): Promise<void> {
  if (running) {
    queuedRun = true;
    return;
  }
  if (!navigator.onLine) {
    emit({ state: 'offline', message: null });
    await refreshCounts();
    return;
  }

  running = true;
  emit({ state: 'syncing', error: null, message: null });
  try {
    await push();

    const done = await getMeta<boolean>('bootstrapDone', false);
    if (!done) {
      await bootstrap((msg) => emit({ message: msg }));
    } else {
      const { needsBootstrap } = await pullDelta();
      if (needsBootstrap) {
        emit({ message: '同期をやり直しています…' });
        await bootstrap((msg) => emit({ message: msg }));
      }
    }

    const at = new Date().toISOString();
    await setMeta('lastSyncAt', at);
    await refreshCounts();
    emit({ state: 'idle', lastSyncAt: at, message: null, error: null });
    bumpData();
  } catch (e) {
    await refreshCounts();
    if (e instanceof ReauthRequiredError) {
      emit({ state: 'reauth', message: null, error: null });
    } else if (e instanceof OfflineError || !navigator.onLine) {
      emit({ state: 'offline', message: null, error: null });
    } else {
      emit({
        state: 'error',
        message: null,
        error: e instanceof Error ? e.message : String(e),
      });
    }
    if (opts.manual) throw e;
  } finally {
    running = false;
    if (queuedRun) {
      queuedRun = false;
      void sync();
    }
  }
}

/** 失敗したopを手動で再試行する。 */
export async function retryFailed(): Promise<void> {
  const db = await getDb();
  const failed = await db.getAllFromIndex('outbox', 'by_status', 'failed');
  const now = new Date().toISOString();
  for (const op of failed) {
    await db.put('outbox', { ...op, status: 'pending', attempts: 0, nextAttemptAt: now });
  }
  await refreshCounts();
  await sync({ manual: true });
}

export async function discardOp(opId: number): Promise<void> {
  const db = await getDb();
  await db.delete('outbox', opId);
  await refreshCounts();
}

export async function clearConflictBadge(): Promise<void> {
  const db = await getDb();
  await db.put('meta', { key: 'conflictCount', value: 0 });
  await refreshCounts();
}

/** ローカルを消さずに OneDrive から全件取り込み直す。delta の取りこぼし対策。 */
export async function resyncFromCloud(): Promise<void> {
  emit({ state: 'syncing', message: '取り込み直しています…', error: null });
  try {
    await refetchAll((msg) => emit({ message: msg }));
    const at = new Date().toISOString();
    await setMeta('lastSyncAt', at);
    emit({ state: 'idle', lastSyncAt: at, message: null });
    bumpData();
  } catch (e) {
    emit({ state: 'error', message: null, error: e instanceof Error ? e.message : String(e) });
    throw e;
  }
}

/** ローカルを捨ててOneDriveから作り直す（§15.3）。 */
export async function rebuildLocal(): Promise<void> {
  const db = await getDb();
  for (const store of ['entries', 'media', 'strokes', 'tombstones', 'dailies', 'reviews', 'taskEntries'] as const) {
    await db.clear(store);
  }
  await db.delete('meta', 'deltaLink');
  await db.delete('meta', 'bootstrapDone');
  bumpData();
  await sync({ manual: true });
}

let debounceTimer: number | undefined;
let periodicTimer: number | undefined;
let started = false;

/** 同期のトリガーを設置する（§7.1）。 */
export function startSyncEngine(): () => void {
  if (started) return () => undefined;
  started = true;

  void refreshCounts().then(() => sync());

  const onOnline = () => void sync();
  const onOffline = () => emit({ state: 'offline' });
  const onVisible = async () => {
    if (document.visibilityState !== 'visible') return;
    const last = await getMeta<string | null>('lastSyncAt', null);
    if (!last || Date.now() - Date.parse(last) > VISIBILITY_MIN_GAP_MS) void sync();
  };

  const offOutbox = onOutboxChanged(() => scheduleSync());

  window.addEventListener('online', onOnline);
  window.addEventListener('offline', onOffline);
  document.addEventListener('visibilitychange', onVisible);
  periodicTimer = window.setInterval(() => {
    if (document.visibilityState === 'visible') void sync();
  }, PERIODIC_MS);

  return () => {
    offOutbox();
    window.removeEventListener('online', onOnline);
    window.removeEventListener('offline', onOffline);
    document.removeEventListener('visibilitychange', onVisible);
    window.clearInterval(periodicTimer);
    started = false;
  };
}

/** 記事の保存・削除の直後に呼ぶ。デバウンスして束ねる（§7.1）。 */
export function scheduleSync(): void {
  window.clearTimeout(debounceTimer);
  debounceTimer = window.setTimeout(() => void sync(), DEBOUNCE_MS);
  void refreshCounts();
}
